import type { FormItemRule, NFormItem } from 'naive-ui'
import { isArray, isClient, timeFormat, timeStamp, validatorPhone } from '@qcwp/utils'
import { cityId, provinceId } from '@qcwp/common'
import { useGetRouteParam, useGetRouteQuery } from '~~/src/common/function/url'
import { validateHandle } from '~~/src/common/function/form'
import { CAR_TEST_DRIVE_SITE_META } from '~~/src/common/site'
import { LogicAddXjSj } from '~~/src/logic/dealer/xjsj'
import type { DealerAddXjSjParams } from '~~/src/server/modules/car/types'
import type { AreaSelectCascaderSelectedType } from '~~/src/components/base/areaSelect/hooks/useAreaCascaderComponent'
import { LogicTestDrive } from '~~/src/logic/car/test-drive'
import { useAuth } from '~/store/auth'
import { getLocation } from '~~/src/server/modules/base'
import type { CascaderSelectedType } from '~~/src/components/page/series/cascader/hook'

export interface TestDriveForm {
  dealerIds: string[]
  carId: [string | undefined, string | undefined, string | undefined, string | undefined]
  carBrandName: string
  carVendorName: string
  carSeriesName: string
  carModelName: string
  area: string
  provinceId: string | undefined
  cityId: string | undefined
  name: string
  sex: 1 | 2
  phone: string
  time?: string
  drivingLicense?: string[] // 驾驶证图片
  remark?: string
}

const timeOptions = [
  {
    label: '一周内',
    value: '1',
  },
  {
    label: '半个月内',
    value: '2',
  },
  {
    label: '3个月内',
    value: '3',
  },
]
const rules = {
  carId: [
    {
      required: true,
      validator: (_rule: FormItemRule, value: string) => {
        return new Promise<void>((resolve, reject) => {
          if (!value[0] || !value[1])
            reject(Error('请选择车系'))
          else
            resolve()
        })
      },
      trigger: ['blur'],
    },
  ],
  name: [
    {
      required: true,
      validator: (_rule: FormItemRule, value: string) => {
        return new Promise<void>((resolve, reject) => {
          if (!value)
            reject(Error('请输入联系人'))
          else
            resolve()
        })
      },
      trigger: ['blur'],
    },
  ],
  phone: [
    {
      required: true,
      validator: (rule: FormItemRule, value: string) => {
        return new Promise<void>((resolve, reject) => {
          if (!value)
            reject(Error('请输入手机号'))
          if (!validatorPhone(value))
            reject(Error('请输入正确手机号'))
          else
            resolve()
        })
      },
      trigger: ['input', 'blur'],
    },
  ],
  cityId: [
    {
      required: true,
      validator: (rule: FormItemRule, value: string) => {
        return new Promise<void>((resolve, reject) => {
          if (!value)
            reject(Error('请选择地区'))
          else
            resolve()
        })
      },
      trigger: ['change'],
    },
  ],
}
function formatTime(type?: string) {
  const currentTime = timeFormat(undefined, 'YYYY-MM-DD HH:mm:ss')

  switch (type) {
    case '1':
      return `${timeStamp(currentTime, 1, 'add', 'week', 'YYYY-MM-DD HH:mm:ss')}`
    case '2':
      return `${timeStamp(currentTime, 15, 'add', 'day', 'YYYY-MM-DD HH:mm:ss')}`
    case '3':
      return `${timeStamp(currentTime, 3, 'add', 'month', 'YYYY-MM-DD HH:mm:ss')}`
    default:
      return currentTime
  }
}

/**
 * 试驾表单页
 */
export function useTestDrivePage() {
  const $bus = useNuxtApp().$bus as any

  const shareBase = ref<ShareBase | null>(null)
  const { siteMeta, changeMeta } = useSite(CAR_TEST_DRIVE_SITE_META)

  const testdriveInviteId = useGetRouteParam('id', 'string')
  const brandId = useGetRouteQuery('carBrandId', 'string')
  const brandName = useGetRouteQuery('carBrandName', 'string')
  const vendorId = useGetRouteQuery('carVendorId', 'string')
  const vendorName = useGetRouteQuery('carVendorName', 'string')
  const seriesId = useGetRouteQuery('carSeriesId', 'string')
  const seriesName = useGetRouteQuery('carSeriesName', 'string')
  const modelId = useGetRouteQuery('carModelId', 'string')
  const modelName = useGetRouteQuery('carModelName', 'string')

  const { testDriveInfo, serverHandler } = LogicTestDrive()

  const authStore = useAuth()
  const formRef = ref()
  const formValue = ref<TestDriveForm>({
    dealerIds: [],
    carId: [brandId.value, vendorId.value, seriesId.value, modelId.value],
    carBrandName: brandName.value || '',
    carVendorName: vendorName.value || '',
    carSeriesName: seriesName.value || '',
    carModelName: modelName.value || '',
    area: '',
    provinceId: undefined,
    cityId: undefined,
    name: '',
    sex: 1,
    phone: '',
    time: '',
    drivingLicense: [], // 驾驶证图片
    remark: '',
  })

  const loading = ref(false)
  const completeState = ref({
    show: false,
    isComplete: false,
    auto: false,
  })
  function showMpQrcode(show = true, auto = false) {
    completeState.value = {
      ...completeState.value,
      show,
      auto,
    }
  }
  async function submitHandle() {
    try {
      const { isOk } = await validateHandle(formRef)
      if (!isOk)
        return
      loading.value = true
      await LogicAddXjSj(formatParams(formValue.value), true)
      vantDialog.close()
      completeState.value.isComplete = true
      showMpQrcode(true, true)
    }
    catch (error) {
      console.error(error)
      if (isArray(error))
        document.querySelector('.n-form-item-blank--error')?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
    }
    finally {
      loading.value = false
    }
  }

  function formatParams(data: typeof formValue.value): DealerAddXjSjParams {
    const carModelId = data.carId?.[3] === '-1' ? undefined : data.carId?.[3]
    const time = formatTime(data.time)
    return {
      carDealerIds: data.dealerIds.length ? data.dealerIds : undefined,
      carModelNakedPrice: undefined, // 车型裸车价
      carBrandId: data.carId?.[0] || undefined,
      carBrandName: data.carBrandName || '',
      carVendorId: data.carId?.[1] || undefined,
      carVendorName: data.carVendorName || '',
      carSeriesId: data.carId?.[2] || undefined,
      carSeriesName: data.carSeriesName || '',
      carModelId: carModelId || undefined,
      carModelName: data.carModelName || '',
      customTargetModel: data.carModelName || undefined,
      enquiryTestdriveType: '2', // 1=询价,2=试驾
      provinceId: data.provinceId,
      cityId: data.cityId,
      userName: data.name,
      userPhone: data.phone,
      userSex: data.sex ? String(data.sex) : undefined,
      testdriveInviteId: (testDriveInfo.value?.publishStatus === '1'
      && brandId.value
      && vendorId.value
      && seriesId.value)
        ? testdriveInviteId.value
        : undefined,
      testdriveTimeWithin: time,
      remark: data.remark,
      drivingLicenseFrontImg: data.drivingLicense?.[0],
    }
  }

  const seriesFormItemRef = ref<typeof NFormItem | null>(null)
  const areaFormItemRef = ref<typeof NFormItem | null>(null)
  const areaSelectCascaderRef = ref<any>(null)
  function selected(data: CascaderSelectedType) {
    formValue.value.carId = [data.brand?.id || '', data.series?.carVendorId || '', data.series?.id || '', data.model?.id || '']

    formValue.value.carBrandName = data.brand?.brandName || ''
    formValue.value.carVendorName = data.series?.carVendorName || ''
    formValue.value.carSeriesName = data.series?.carSeriesName || ''
    formValue.value.carModelName = data.model?.carModelName || ''
  }
  /**
   * 地区初始化
   */
  function areaInit(loc: any) {
    const provinceName = loc?.province || ''
    const cityName = loc?.city || ''

    const pid = provinceId(provinceName, true)
    const cid = cityId(cityName, true)

    setArea({ provinceId: pid, provinceName, cityId: cid, cityName })
    areaSelectCascaderRef.value?.initAreaHandle(pid, cid, true)
  }
  /**
   * 更新地区数据
   */
  function setArea(data: { provinceId?: string; provinceName?: string; cityId?: string; cityName?: string }) {
    const { provinceId, provinceName, cityId, cityName } = data

    if (provinceId === formValue.value.provinceId && cityId === formValue.value.cityId)
      return

    formValue.value.provinceId = provinceId
    formValue.value.cityId = cityId
    formValue.value.area = ''

    if (provinceName)
      formValue.value.area = provinceName.replace('省', '').replace('市', '')
    if (cityName)
      formValue.value.area += cityName.replace('市', '')
  }

  function selectArea(params: AreaSelectCascaderSelectedType) {
    formValue.value.dealerIds = params.dealers.map(item => item.id)
    if (!params)
      return setArea({})
    if (params.province?.id
      && params.province.id !== formValue.value.provinceId
      && params.city?.id
      && params.city.id !== formValue.value.cityId
    ) {
      setArea({
        provinceId: params.province.id,
        provinceName: params.province.name,
        cityId: params.city.id,
        cityName: params.city.name,
      })
    }
  }

  async function initHandler() {
    const messageStopFn: any = null

    let stops: any[] = [
      watch(() => formValue.value.carId, () => {
        seriesFormItemRef.value?.validate()
      }, { deep: true }),
      watch(() => formValue.value.cityId, () => {
        areaFormItemRef.value?.validate()
      }),
      // 选择车系组件
      watch(areaSelectCascaderRef, () => {
        areaSelectCascaderRef.value?.initHandle()
      }),
      watch([brandId, vendorId, seriesId, modelId], (data) => {
        if (
          brandId.value !== formValue.value.carId[0]
          || vendorId.value !== formValue.value.carId[1]
          || seriesId.value !== formValue.value.carId[2]
          || modelId.value !== formValue.value.carId[3]
        )
          formValue.value.carId = data
      }),
      watch(() => [brandName.value, vendorName.value, seriesName.value, modelName.value], (data) => {
        formValue.value.carBrandName = data[0]
        formValue.value.carVendorName = data[1]
        formValue.value.carSeriesName = data[2]
        formValue.value.carModelName = data[3]
      }),
    ]
    // 获取IP定位地址
    if (isClient) {
      getLocation().then((res) => {
        const { proName, cityName } = res.data
        proName && cityName && areaInit({ province: proName, city: cityName })
      }).catch(console.error)
      onMounted(() => {
        const { USER_INFO } = storeToRefs(authStore)
        if (USER_INFO.value.type === 'LoginUserInfo' && USER_INFO.value.phone)
          formValue.value.phone = USER_INFO.value.phone
      })
    }

    onUnmounted(() => {
      stops.forEach(fn => fn?.())

      messageStopFn?.()
      stops = null!
    })

    try {
      testdriveInviteId.value && await serverHandler(testdriveInviteId.value)

      if (!testDriveInfo.value)
        return

      const carInfo = new Set<string>()
      carInfo.add(testDriveInfo.value.carBrandName)
      carInfo.add(testDriveInfo.value.carVendorName)
      carInfo.add(testDriveInfo.value.carSeriesName)
      carInfo.add(testDriveInfo.value.carModelName)

      const title = `${Array.from(carInfo).join('')}预约试驾 - 汽车网评`
      const description = title.replace(' - 汽车网评', '')
      changeMeta({
        title: title || siteMeta.value.title,
        description: description || siteMeta.value.description,
        keywords: [...Array.from(carInfo), '预约试驾', '汽车网评'] || siteMeta.value.keywords,
      })
      if (isClient && testDriveInfo.value) {
        shareBase.value = {
          url: window.location.href,
          title,
          content: description,
          images: [testDriveInfo.value.coverUrl],
        }
        useShare(shareBase.value)
      }
    }
    catch (error) {
      showDialogError(error)
    }
    if (testDriveInfo.value) {
      formValue.value.carId = [
        testDriveInfo.value.carBrandId,
        testDriveInfo.value.carVendorId || vendorId.value,
        testDriveInfo.value.carSeriesId,
        testDriveInfo.value.carModelId,
      ]
    }
    if (!authStore.IS_LOGIN && isClient)
      useNoLoginVantDialog(undefined, { type: 'popup' })
  }

  function showLoginPopup() {
    $bus.emit('SHOW_LOGIN', {
      show: true,
      redirect: window.location.href,
    })
  }

  return {
    siteMeta,
    brandId,
    brandName,
    vendorId,
    vendorName,
    seriesId,
    seriesName,
    modelId,
    modelName,

    testDriveInfo,
    timeOptions,
    rules: {
      ...rules,
      drivingLicense: [{
        required: true,
        validator: (_rule: FormItemRule, value: string) => {
          return new Promise<void>((resolve, reject) => {
            if (!value?.[0])
              reject(Error('请上传驾驶证正面照片'))
            else
              resolve()
          })
        },
        trigger: ['blur'],
      }],
    },
    formRef,
    seriesFormItemRef,
    areaFormItemRef,
    formValue,
    submitHandle,

    areaSelectCascaderRef,
    selectArea,
    selected,
    completeState,
    showMpQrcode,

    initHandler,

    IS_LOGIN: storeToRefs(authStore).IS_LOGIN,
    showLoginPopup,
  }
}

/**
 * 试驾表单组件
 */
export function useTestDriveComponent() {
  const $bus = useNuxtApp().$bus as any

  const authStore = useAuth()
  const formRef = ref()
  const formValue = ref<TestDriveForm>({
    dealerIds: [],
    carId: [undefined, undefined, undefined, undefined],
    carBrandName: '',
    carVendorName: '',
    carSeriesName: '',
    carModelName: '',
    area: '',
    provinceId: undefined,
    cityId: undefined,
    name: '',
    sex: 1,
    phone: '',
    time: '3',
    remark: '',
  })

  const loading = ref(false)
  const completeState = ref({
    show: false,
    isComplete: false,
    auto: false,
  })
  function showMpQrcode(show = true, auto = false) {
    completeState.value = {
      ...completeState.value,
      show,
      auto,
    }
    if (!show && !auto)
      isClient && window.scrollTo(0, 0)
  }
  async function submitHandle() {
    try {
      const { isOk } = await validateHandle(formRef)
      if (!isOk)
        return
      loading.value = true
      await LogicAddXjSj(formatParams(formValue.value), true)
      vantDialog.close()
      completeState.value.isComplete = true
      showMpQrcode(true, true)
    }
    catch (error) {
      console.error(error)
    }
    finally {
      loading.value = false
    }
  }

  function formatParams(data: typeof formValue.value): DealerAddXjSjParams {
    const carModelId = data.carId?.[3] === '-1' ? undefined : data.carId?.[3]
    const time = formatTime(data.time)
    return {
      carDealerIds: data.dealerIds.length ? data.dealerIds : undefined,
      carModelNakedPrice: undefined, // 车型裸车价
      carBrandId: data.carId?.[0] || undefined,
      carBrandName: data.carBrandName || '',
      carVendorId: data.carId?.[1] || undefined,
      carVendorName: data.carVendorName || '',
      carSeriesId: data.carId?.[2] || undefined,
      carSeriesName: data.carSeriesName || '',
      carModelId: carModelId || undefined,
      carModelName: data.carModelName || '',
      customTargetModel: data.carModelName || undefined,
      enquiryTestdriveType: '2', // 1=询价,2=试驾
      provinceId: data.provinceId,
      cityId: data.cityId,
      userName: data.name,
      userPhone: data.phone,
      userSex: data.sex ? String(data.sex) : undefined,
      testdriveInviteId: undefined,
      testdriveTimeWithin: time,
      remark: data.remark,
      link: window.location.href,
    }
  }

  const seriesFormItemRef = ref<typeof NFormItem | null>(null)
  const areaFormItemRef = ref<typeof NFormItem | null>(null)
  const areaSelectCascaderRef = ref<any>(null)
  function selected(data: CascaderSelectedType) {
    formValue.value.carId = [data.brand?.id || '', data.series?.carVendorId || '', data.series?.id || '', data.model?.id || '']

    formValue.value.carBrandName = data.brand?.brandName || ''
    formValue.value.carVendorName = data.series?.carVendorName || ''
    formValue.value.carSeriesName = data.series?.carSeriesName || ''
    formValue.value.carModelName = data.model?.carModelName || ''
  }
  /**
   * 地区初始化
   */
  function areaInit(loc: any) {
    const provinceName = loc?.province || ''
    const cityName = loc?.city || ''
    const pid = provinceId(provinceName, true)
    const cid = cityId(cityName, true)

    setArea({ provinceId: pid, provinceName, cityId: cid, cityName })
    areaSelectCascaderRef.value?.initAreaHandle(pid, cid, true)
  }
  /**
   * 更新地区数据
   */
  function setArea(data: { provinceId?: string; provinceName?: string; cityId?: string; cityName?: string }) {
    const { provinceId, provinceName, cityId, cityName } = data

    if (provinceId === formValue.value.provinceId && cityId === formValue.value.cityId)
      return

    formValue.value.provinceId = provinceId
    formValue.value.cityId = cityId
    formValue.value.area = ''

    if (provinceName)
      formValue.value.area = provinceName.replace('省', '').replace('市', '')
    if (cityName)
      formValue.value.area += cityName.replace('市', '')
  }

  function selectArea(params: AreaSelectCascaderSelectedType) {
    formValue.value.dealerIds = params.dealers.map(item => item.id)
    if (!params)
      return setArea({})
    if (params.province?.id
      && params.province.id !== formValue.value.provinceId
      && params.city?.id
      && params.city.id !== formValue.value.cityId
    ) {
      setArea({
        provinceId: params.province.id,
        provinceName: params.province.name,
        cityId: params.city.id,
        cityName: params.city.name,
      })
    }
  }
  function showLoginPopup() {
    $bus.emit('SHOW_LOGIN', {
      show: true,
      redirect: window.location.href,
    })
  }
  async function initHandler() {
    const messageStopFn: any = null

    let stops: any[] = [
      watch(() => formValue.value.carId, () => {
        seriesFormItemRef.value?.validate()
      }, { deep: true }),
      watch(() => formValue.value.cityId, () => {
        areaFormItemRef.value?.validate()
      }),
      // 选择车系组件
      watch(areaSelectCascaderRef, () => {
        areaSelectCascaderRef.value?.initHandle()
      }),
    ]
    // 获取IP定位地址
    if (isClient) {
      onMounted(() => {
        const { USER_INFO } = storeToRefs(authStore)
        if (USER_INFO.value.type === 'LoginUserInfo' && USER_INFO.value.phone)
          formValue.value.phone = USER_INFO.value.phone
      })
      getLocation().then((res) => {
        const { proName, cityName } = res.data
        proName && cityName && areaInit({ province: proName, city: cityName })
      }).catch(console.error)
    }

    onUnmounted(() => {
      stops.forEach(fn => fn?.())

      messageStopFn?.()
      stops = null!
    })
  }

  return {
    timeOptions,
    rules,
    formRef,
    seriesFormItemRef,
    areaFormItemRef,
    formValue,
    submitHandle,

    areaSelectCascaderRef,
    selectArea,
    selected,
    completeState,
    showMpQrcode,

    initHandler,

    IS_LOGIN: storeToRefs(authStore).IS_LOGIN,
    showLoginPopup,
  }
}
